/**
 * BANK-IBAN-R3.10 — staging-owner-only runtime strip.
 * Production customers never see this (VITE_STAGING_DIAGNOSTICS !== 1).
 */
import { useState } from "react";
import type { CustomerBootstrapPayload } from "./customer-runtime-bootstrap-v2.js";
import { CLIENT_APP_REV } from "./customer-runtime-handshake-v2.js";
import { pdpTimings } from "./pdp-seed-perf-v2.js";
import { telegramUserId } from "./customer-profile-v2.js";

const OWNER_ALLOWLIST = new Set(["475734587"]);

export function isOwnerRuntimeStripAllowed(): boolean {
  if (String(import.meta.env.VITE_STAGING_DIAGNOSTICS ?? "").trim() !== "1") return false;
  const uid = telegramUserId();
  if (uid && OWNER_ALLOWLIST.has(uid)) return true;
  try {
    const owner = new URLSearchParams(window.location.search).get("owner_tg");
    return Boolean(owner && OWNER_ALLOWLIST.has(owner));
  } catch {
    return false;
  }
}

function jsHash(): string {
  try {
    const scripts = Array.from(document.querySelectorAll("script[src]"));
    const hit = scripts.map((s) => s.getAttribute("src") ?? "").find((src) => /\/assets\/index-/.test(src));
    const m = hit?.match(/index-([^/.]+)\.js/);
    return m?.[1] ?? "n/a";
  } catch {
    return "n/a";
  }
}

export function OwnerRuntimeStripV2(props: {
  buildId: string;
  appRev: string;
  bootstrap: CustomerBootstrapPayload | null;
  consentAccepted: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!isOwnerRuntimeStripAllowed()) return null;
  const boot = props.bootstrap;
  const payment = boot?.payment.mode ?? "UNKNOWN";
  const customer = boot?.customer_id ?? (telegramUserId() ? `tg:${telegramUserId()}` : "anon");
  const timings = pdpTimings();
  return (
    <div
      data-testid="sv2-owner-runtime-strip"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "108px",
        zIndex: 900,
        fontSize: 11,
        lineHeight: 1.35,
        background: "rgba(15,23,42,0.94)",
        color: "#e2e8f0",
        padding: "6px 8px",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent",
          border: 0,
          color: "inherit",
          font: "inherit",
          padding: 0,
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
        }}
      >
        R3.10 · REV: {props.appRev || CLIENT_APP_REV || "n/a"} · JS: {jsHash()} · PAY: {payment} · USER: {customer}
      </button>
      {open ? (
        <pre style={{ margin: "6px 0 0", whiteSpace: "pre-wrap", fontFamily: "ui-monospace, monospace" }}>
{`origin: ${typeof window !== "undefined" ? window.location.origin : ""}
bootstrap_rev: ${boot?.customer_runtime?.expected_app_rev ?? boot?.app_rev ?? "n/a"}
client_rev: ${CLIENT_APP_REV || props.appRev}
canonical: ${boot?.customer_runtime?.canonical_origin ?? "n/a"}
tap_to_route_ms: ${timings.tap_to_route_ms ?? "n/a"}
tap_to_seed_paint_ms: ${timings.tap_to_seed_paint_ms ?? "n/a"}
tap_to_detail_ms: ${timings.tap_to_detail_ms ?? "n/a"}`}
        </pre>
      ) : null}
    </div>
  );
}
